export enum LocaleCode {
  Ar = 'ar',
  De = 'de',
  En = 'en',
  Es = 'es',
  Fil = 'fil',
  Fr = 'fr',
  Hi = 'hi',
  Id = 'id',
  It = 'it',
  Ja = 'ja',
  Ko = 'ko',
  Ms = 'ms',
  Pt = 'pt',
  Ru = 'ru',
  Th = 'th',
  Tr = 'tr',
  Ur = 'ur',
  Vi = 'vi',
  Zh = 'zh',
  ZhCn = 'zh-cn',
  ZhHk = 'zh-hk',
  ZhTw = 'zh-tw',
}

export type LocaleDefinition = {
  code: LocaleCode
  id: string
  name: string
  language: string
  dir: 'ltr' | 'rtl'
}

export const LOCALES: readonly LocaleDefinition[] = [
  {
    code: LocaleCode.ZhCn,
    id: '156',
    name: '简体中文',
    language: 'zh-CN',
    dir: 'ltr',
  },
  {
    code: LocaleCode.En,
    id: '840',
    name: 'English',
    language: 'en-US',
    dir: 'ltr',
  },
  {
    code: LocaleCode.Zh,
    id: '344',
    name: '繁體中文',
    language: 'zh',
    dir: 'ltr',
  },
  {
    code: LocaleCode.Ar,
    id: '682',
    name: 'العربية',
    language: 'ar-SA',
    dir: 'rtl',
  },
  {
    code: LocaleCode.De,
    id: '276',
    name: 'Deutsch',
    language: 'de-DE',
    dir: 'ltr',
  },
  {
    code: LocaleCode.Es,
    id: '724',
    name: 'Español',
    language: 'es-ES',
    dir: 'ltr',
  },
  {
    code: LocaleCode.Fil,
    id: '608',
    name: 'Filipino',
    language: 'fil-PH',
    dir: 'ltr',
  },
  {
    code: LocaleCode.Fr,
    id: '250',
    name: 'Français',
    language: 'fr-FR',
    dir: 'ltr',
  },
  {
    code: LocaleCode.Hi,
    id: '356',
    name: 'हिन्दी',
    language: 'hi-IN',
    dir: 'ltr',
  },
  {
    code: LocaleCode.Id,
    id: '360',
    name: 'Bahasa Indonesia',
    language: 'id-ID',
    dir: 'ltr',
  },
  {
    code: LocaleCode.It,
    id: '380',
    name: 'Italiano',
    language: 'it-IT',
    dir: 'ltr',
  },
  {
    code: LocaleCode.Ja,
    id: '392',
    name: '日本語',
    language: 'ja-JP',
    dir: 'ltr',
  },
  {
    code: LocaleCode.Ko,
    id: '410',
    name: '한국어',
    language: 'ko-KR',
    dir: 'ltr',
  },
  {
    code: LocaleCode.Ms,
    id: '458',
    name: 'Bahasa Melayu',
    language: 'ms-MY',
    dir: 'ltr',
  },
  {
    code: LocaleCode.Pt,
    id: '620',
    name: 'Português',
    language: 'pt-PT',
    dir: 'ltr',
  },
  {
    code: LocaleCode.Ru,
    id: '643',
    name: 'Русский',
    language: 'ru-RU',
    dir: 'ltr',
  },
  {
    code: LocaleCode.Th,
    id: '764',
    name: 'ไทย',
    language: 'th-TH',
    dir: 'ltr',
  },
  {
    code: LocaleCode.Tr,
    id: '792',
    name: 'Türkçe',
    language: 'tr-TR',
    dir: 'ltr',
  },
  {
    code: LocaleCode.Ur,
    id: '586',
    name: 'اردو',
    language: 'ur-PK',
    dir: 'rtl',
  },
  {
    code: LocaleCode.Vi,
    id: '704',
    name: 'Tiếng Việt',
    language: 'vi-VN',
    dir: 'ltr',
  },
] as const

export const DEFAULT_LOCALE: LocaleCode = LocaleCode.ZhCn

export const getLocaleCountryCode = (locale: unknown) => {
  const code = typeof locale === 'string' ? locale.trim().toLowerCase() : ''
  return LOCALES.find((localeDefinition) => String(localeDefinition.code) === code)?.id || ''
}

const ensureLocaleDefinition = (localeDefinition: LocaleDefinition | undefined) => {
  if (localeDefinition === undefined) {
    throw new Error('Expected at least one locale definition.')
  }

  return localeDefinition
}

const LOCALE_CODES: LocaleCode[] = []
const LOCALE_SET = new Set<string>()
const LOCALE_MAP = new Map<LocaleCode, LocaleDefinition>()

for (const localeDefinition of LOCALES) {
  LOCALE_CODES.push(localeDefinition.code)
  LOCALE_SET.add(localeDefinition.code)
  LOCALE_MAP.set(localeDefinition.code, localeDefinition)
}

export const normalizeLocaleCodeValue = (value: string) => value.trim().replaceAll('_', '-').toLowerCase()

export const getLocaleCodes = () => [...LOCALE_CODES]

export const isLocaleCode = (value: string): value is LocaleCode => LOCALE_SET.has(value)

export const normalizeLocaleParam = (value: unknown): LocaleCode | null => {
  const rawValue: unknown = Array.isArray(value) ? value.at(0) : value
  const normalizedValue = typeof rawValue === 'string' ? normalizeLocaleCodeValue(rawValue) : null

  return normalizedValue !== null && isLocaleCode(normalizedValue) ? normalizedValue : null
}

export const DEFAULT_LOCALE_META: LocaleDefinition = ensureLocaleDefinition(
  LOCALE_MAP.get(DEFAULT_LOCALE) ?? LOCALES[0],
)

export const getLocaleMeta = (locale: LocaleCode): LocaleDefinition => LOCALE_MAP.get(locale) ?? DEFAULT_LOCALE_META

export const RTL_LOCALES: ReadonlySet<LocaleCode> = new Set(LOCALES.filter((l) => l.dir === 'rtl').map((l) => l.code))

export const isRtlLocale = (locale: LocaleCode): boolean => RTL_LOCALES.has(locale)

export const stripLocalePrefix = (path: string) => {
  const pattern = new RegExp(`^/(${getLocaleCodes().join('|')})(?=/|$)`)
  const strippedPath = path.replace(pattern, '')

  return strippedPath.startsWith('/') ? strippedPath : `/${strippedPath}`
}

export const localePath = (locale: LocaleCode, path = '/') => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const strippedPath = stripLocalePrefix(normalizedPath)

  return strippedPath === '/' ? `/${locale}` : `/${locale}${strippedPath}`
}

export const switchLocalePath = (currentPath: string, targetLocale: LocaleCode) => {
  const [pathnameWithLocale, suffix = ''] = currentPath.split(/([?#].*)/, 2)
  const nextPath = localePath(targetLocale, pathnameWithLocale)

  return `${nextPath}${suffix}`
}
